import React, { useEffect, useState } from "react";
import {
  getServiceComponents,
  createServiceComponent,
  updateServiceComponent,
  deleteServiceComponent,
  getServiceComponentById,
} from "../../apis/serviceComponents";
import { getServices } from "../../apis/service";
import { getLabTests } from "../../apis/LabOrder";

interface ComponentItem {
  itemType: "service" | "lab_test";
  itemId: number;
}

interface ServiceComponent {
  id: number;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  items?: { id: number; itemType: string; itemId: number }[];
}

const emptyForm = {
  name: "",
  description: "",
  displayOrder: 0,
  isActive: true,
};

type ItemOption = { id: number; label: string };

const ServiceComponentsList: React.FC = () => {
  const [components, setComponents] = useState<ServiceComponent[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Item selection state
  const [selectedItems, setSelectedItems] = useState<ComponentItem[]>([]);
  const [services, setServices] = useState<ItemOption[]>([]);
  const [labTests, setLabTests] = useState<ItemOption[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"service" | "lab_test">("service");

  useEffect(() => {
    fetchComponents();
  }, []);

  const unwrap = (res: any): any[] => {
    const d = res?.data ?? res;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  };

  const fetchComponents = async () => {
    setLoading(true);
    try {
      const res: any = await getServiceComponents({ per_page: 200 });
      setComponents(unwrap(res));
    } finally {
      setLoading(false);
    }
  };

  const fetchItemOptions = async () => {
    setItemsLoading(true);
    try {
      const [svcRes, labRes]: any[] = await Promise.all([
        getServices({ per_page: 500 }),
        getLabTests({ per_page: 500 }),
      ]);
      setServices(unwrap(svcRes).map((s: any) => ({ id: s.id, label: s.name })));
      setLabTests(unwrap(labRes).map((t: any) => ({ id: t.id, label: t.name })));
    } finally {
      setItemsLoading(false);
    }
  };

  const openCreate = async () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setSelectedItems([]);
    setModalOpen(true);
    fetchItemOptions();
  };

  const openEdit = async (id: number) => {
    setEditingId(id);
    setModalOpen(true);
    fetchItemOptions();
    try {
      const res: any = await getServiceComponentById(id);
      const d = res?.data ?? res;
      setForm({
        name: d.name ?? "",
        description: d.description ?? "",
        displayOrder: d.displayOrder ?? d.display_order ?? 0,
        isActive: d.isActive ?? d.is_active ?? true,
      });
      const rawItems: any[] = d.items ?? [];
      setSelectedItems(
        rawItems
          .filter((i) => (i.itemType ?? i.item_type) !== "room_type")
          .map((i) => ({
            itemType: (i.itemType ?? i.item_type) as "service" | "lab_test",
            itemId: i.itemId ?? i.item_id,
          }))
      );
    } catch {
      setSelectedItems([]);
    }
  };

  const toggleItem = (type: "service" | "lab_test", id: number) => {
    setSelectedItems((prev) => {
      const exists = prev.some((x) => x.itemType === type && x.itemId === id);
      if (exists) return prev.filter((x) => !(x.itemType === type && x.itemId === id));
      return [...prev, { itemType: type, itemId: id }];
    });
  };

  const isSelected = (type: string, id: number) =>
    selectedItems.some((x) => x.itemType === type && x.itemId === id);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        displayOrder: form.displayOrder,
        isActive: form.isActive,
        items: selectedItems.map((i) => ({ itemType: i.itemType, itemId: i.itemId })),
      };
      if (editingId) {
        await updateServiceComponent(editingId, payload);
      } else {
        await createServiceComponent(payload);
      }
      setModalOpen(false);
      fetchComponents();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this service component?")) return;
    await deleteServiceComponent(id);
    fetchComponents();
  };

  const tabOptions: { key: "service" | "lab_test"; label: string; options: ItemOption[] }[] = [
    { key: "service", label: "Visit Services", options: services },
    { key: "lab_test", label: "Lab Tests", options: labTests },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Service Components</h1>
          <p className="mt-2 text-sm text-gray-700">
            Flexible groupings used to map your charges to insurance provider claim templates.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Add Component
        </button>
      </div>

      <div className="mt-8 flow-root">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Description</th>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Items Tagged</th>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Order</th>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Active</th>
                <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-sm text-gray-500">Loading…</td>
                </tr>
              ) : components.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-sm text-gray-500">No components yet.</td>
                </tr>
              ) : (
                components.map((c) => (
                  <tr key={c.id}>
                    <td className="py-3 px-3 text-sm font-medium text-gray-900">{c.name}</td>
                    <td className="py-3 px-3 text-sm text-gray-700 max-w-xs truncate">{c.description ?? "—"}</td>
                    <td className="py-3 px-3 text-sm text-gray-700">
                      {(c.items ?? []).length > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {(c.items ?? []).length} item{(c.items ?? []).length !== 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-700">{c.displayOrder ?? 0}</td>
                    <td className="py-3 px-3 text-sm text-gray-700">{c.isActive ? "Yes" : "No"}</td>
                    <td className="py-3 px-3 text-sm text-gray-700">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEdit(c.id)}
                          className="px-3 py-1 text-sm text-white bg-primary rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="px-3 py-1 text-sm text-white bg-red-600 rounded"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-primary text-white">
              <h2 className="text-base font-semibold">
                {editingId ? "Edit Component" : "Add Component"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-white hover:text-gray-200">✕</button>
            </div>

            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Basic fields */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                    placeholder="e.g. Room Accommodation"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.displayOrder}
                    onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>

              {/* Item tagging */}
              <div className="border rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <p className="text-sm font-medium text-gray-700">
                    Tag Items to this Component
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      Items can belong to multiple components
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} selected
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b">
                  {tabOptions.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.key
                          ? "border-primary text-primary"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab.label}
                      {selectedItems.filter((x) => x.itemType === tab.key).length > 0 && (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-primary text-white">
                          {selectedItems.filter((x) => x.itemType === tab.key).length}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="max-h-56 overflow-y-auto p-3">
                  {itemsLoading ? (
                    <p className="text-sm text-gray-500 text-center py-4">Loading…</p>
                  ) : (
                    tabOptions
                      .find((t) => t.key === activeTab)
                      ?.options.map((opt) => (
                        <label
                          key={opt.id}
                          className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected(activeTab, opt.id)}
                            onChange={() => toggleItem(activeTab, opt.id)}
                            className="rounded border-gray-300 text-primary"
                          />
                          <span className="text-sm text-gray-800">{opt.label}</span>
                        </label>
                      ))
                  )}
                  {!itemsLoading &&
                    (tabOptions.find((t) => t.key === activeTab)?.options ?? []).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">No items found.</p>
                    )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="px-4 py-2 text-sm text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
              >
                {saving ? "Saving…" : editingId ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceComponentsList;
